import {
  type ApprovalChain,
  type ChainQuorum,
  DomainError,
  MAX_REVIEW_STEPS,
  parseNewApprovalChain,
  withoutOfficeSteps,
} from '@felix/domain';

/**
 * Form zinciri editörünün saf kuralları (ADR 0071 §2): taslak (yarım seçimler dahil) ile kayıtlı
 * zincir arasında çeviri, doğrulama ve kısa özet. Sunucu her kayıtta zinciri yeniden doğrular.
 * ISM formu ofis onayına gitmez (sahip kararı 2026-10-17; ADR 0073): zincir Master'la biter, eski
 * zincirin ofis adımı taslağa gelmez.
 */

/** Editörün durumu: `custom` kapalıysa onay matrisinin kuralı (hazır ayar). */
export interface ChainDraft {
  custom: boolean;
  preparers: string[];
  prepareQuorum: ChainQuorum;
  /** Ön onaylar, sıralı; seçilmemiş adım `null`. */
  reviews: (string | null)[];
  master: string | null;
}

export interface ChainRole {
  id: string;
  name: string;
}

/** Rütbelerin kısa yazımı (sahip kararı 2026-10-17): ad şirketteki rütbe rolünün adıdır. */
const RANK_SHORT: Readonly<Record<string, string>> = {
  'Chief Officer': 'C/O',
  '2nd Officer': '2/O',
  '3rd Officer': '3/O',
  '4th Officer': '4/O',
  'Chief Engineer': 'C/E',
  '2nd Engineer': '2/E',
  '3rd Engineer': '3/E',
  '4th Engineer': '4/E',
  'Electro-Technical Officer': 'ETO',
  // Eş adlar (ADR 0071 §1)
  'Second Officer': '2/O',
  'Third Officer': '3/O',
  'Fourth Officer': '4/O',
  'Second Engineer': '2/E',
  'Third Engineer': '3/E',
  'Fourth Engineer': '4/E',
};

/** Rolün kısa adı: bilinen rütbede kısaltma, ötekinde adı. */
export function rankShort(name: string): string {
  return RANK_SHORT[name] ?? name;
}

/** Seçim listesinde rolün etiketi: "Third Engineer (3/E)". */
export function rankLabel(name: string): string {
  const short = RANK_SHORT[name];
  return short ? `${name} (${short})` : name;
}

/** Kayıtlı zincirden editör taslağı; zincir yoksa Master varsayılanıyla kapalı taslak. */
export function chainToDraft(chain: ApprovalChain | null, roles: readonly ChainRole[]): ChainDraft {
  const master = roles.find((role) => role.name === 'Master')?.id ?? null;
  if (!chain) {
    return {
      custom: false,
      preparers: [],
      prepareQuorum: 'all',
      reviews: [],
      master,
    };
  }
  const of = (kind: string) => chain.steps.filter((step) => step.kind === kind);
  const prepare = of('prepare')[0];
  return {
    custom: true,
    preparers: [...(prepare?.roleIds ?? [])],
    prepareQuorum: prepare?.quorum ?? 'all',
    reviews: of('review').map((step) => step.roleIds[0] ?? null),
    master: of('master')[0]?.roleIds[0] ?? master,
  };
}

/**
 * Taslaktan kayıtlı zincir: kapalı taslak `null` (matris). Tek rütbeli adımda kural "hepsi"dir
 * (biri ile aynı); boş seçimler doğrulamada yakalanır.
 */
export function draftToChain(draft: ChainDraft): ApprovalChain | null {
  if (!draft.custom) return null;
  const quorum = (roles: readonly string[], chosen: ChainQuorum): ChainQuorum =>
    roles.length > 1 ? chosen : 'all';
  return {
    steps: [
      {
        kind: 'prepare',
        roleIds: [...draft.preparers],
        quorum: quorum(draft.preparers, draft.prepareQuorum),
      },
      ...draft.reviews.map((role) => ({
        kind: 'review' as const,
        roleIds: role ? [role] : [],
        quorum: 'all' as const,
      })),
      { kind: 'master', roleIds: draft.master ? [draft.master] : [], quorum: 'all' },
    ],
  };
}

/** Taslağın hata kodu (`documents.validation.<kod>`); geçerliyse `null`. */
export function chainDraftProblem(draft: ChainDraft): string | null {
  const chain = draftToChain(draft);
  if (!chain) return null;
  if (draft.preparers.length === 0) return 'CHAIN_PREPARER_REQUIRED';
  if (draft.reviews.some((role) => role === null) || draft.master === null) {
    return 'CHAIN_STEP_EMPTY';
  }
  if (draft.reviews.length > MAX_REVIEW_STEPS) return 'INVALID_APPROVAL_CHAIN';
  try {
    parseNewApprovalChain(chain);
  } catch (error) {
    if (error instanceof DomainError) return error.code;
    throw error;
  }
  return null;
}

/**
 * Zincirin kısa özeti: "2/O + 3/O → C/O → Master"; "hepsi" adımında "+",
 * "biri yeter"de "/".
 */
export function chainSummary(chain: ApprovalChain, roleName: (id: string) => string): string {
  return chain.steps
    .map((step) =>
      step.roleIds.map((id) => rankShort(roleName(id))).join(step.quorum === 'all' ? ' + ' : ' / '),
    )
    .join(' → ');
}

/** Ekranda gösterilen zincir: uygulanacak olan, ofis adımı olmadan (ADR 0073). */
export function shownChain(chain: ApprovalChain | null | undefined): ApprovalChain | null {
  return chain ? withoutOfficeSteps(chain) : null;
}

/** İki zincir aynı mı (Edit'te değişti mi). */
export function sameChain(a: ApprovalChain | null, b: ApprovalChain | null): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
